import { useSection } from "@tree-company/tree-query";
import { useEffect, useMemo, useState } from "react";
import * as Sentry from "@sentry/react";
import clsx from "clsx";
import Card from "./card";
import translate from "../locales";
import { PROJECTS } from "../data/data";
import {
  store,
  survey,
  PROGRESS_KEY,
  QUEUE_KEY,
  VIEWED,
  VOTE_YES,
  VOTE_NO,
  projAnswerKey,
} from "../survey";

import instructionImg from "../assets/images/swipe_instruction.png";
import { ArrowRightIcon, BackArrowIcon, CloseIcon, HeartIcon } from "./icons";

const PROJECTS_BY_ID = Object.fromEntries(PROJECTS.map((p) => [p.id, p]));

const MIN_PROJECTS_FOR_OVERVIEW = Number(
  import.meta.env.VITE_OVERVIEW_MIN_PROJECTS || 3,
);

const CARD_ANIMATION_MS = 450;

function formatBudget(budget) {
  return new Intl.NumberFormat("nl-BE", {
    maximumFractionDigits: 0,
  }).format(budget);
}

function readProgress(optionCount) {
  const saved = store.getAnswer(PROGRESS_KEY);
  if (!Number.isInteger(saved) || saved < 0) return 0;
  // Cap at the deck length: a completed deck resumes via the overview, not here.
  return Math.min(saved, optionCount);
}

export default function Swipe({ question }) {
  const [currentResponse, setCurrentResponse] = useState(null);
  const [returnDirection, setReturnDirection] = useState(null);
  const [leaveDirection, setLeaveDirection] = useState(null);
  const { options } = useSection(question);
  // The deck follows the voter's stored queue (least-globally-viewed projects
  // first), not the catalog order of the section options.
  const orderedOptions = useMemo(() => {
    const queue = store.getAnswer(QUEUE_KEY);
    if (!Array.isArray(queue) || queue.length === 0) return options;
    const optionsById = Object.fromEntries(
      options.map((opt) => [opt.value, opt]),
    );
    return queue.map((id) => optionsById[id]).filter(Boolean);
  }, [options]);
  const hasPriorVotes = useMemo(
    () =>
      orderedOptions.some((opt) => {
        const vote = store.getAnswer(projAnswerKey(opt.value));
        return vote === VOTE_YES || vote === VOTE_NO;
      }),
    [orderedOptions],
  );
  const [currentIndex, setCurrentIndex] = useState(() => {
    const progress = readProgress(orderedOptions.length);
    return progress === 0 && hasPriorVotes ? 1 : progress;
  });
  const currentOption = orderedOptions[currentIndex - 1];
  const visibleOptions = [
    currentOption,
    ...orderedOptions.slice(currentIndex, currentIndex + 3),
  ].filter((opt) => opt !== undefined);

  // Mark the top card as viewed the moment it is shown
  useEffect(() => {
    if (currentOption === undefined) return;
    store.setAnswer(PROGRESS_KEY, currentIndex);
    const key = projAnswerKey(currentOption.value);
    if (store.getAnswer(key) === null) {
      store.setAnswer(key, VIEWED);
    }
    store.save().catch((error) => Sentry.captureException(error));
  }, [currentOption, currentIndex]);

  const yesCount = orderedOptions.filter(
    (opt) => store.getAnswer(projAnswerKey(opt.value)) === VOTE_YES,
  ).length;

  const viewCount = orderedOptions.filter(
    (opt) => store.getAnswer(projAnswerKey(opt.value)) !== null,
  ).length;

  const getVote = (option) => {
    const vote = option ? store.getAnswer(projAnswerKey(option.value)) : null;
    return vote === VOTE_YES || vote === VOTE_NO ? vote : null;
  };
  const markedVote = getVote(currentOption);

  const canGoBack = currentIndex > 1;
  const canGoForward =
    currentIndex > 0 &&
    currentIndex <= orderedOptions.length &&
    getVote(currentOption) !== null;

  const handleNavigate = (e, delta) => {
    e.preventDefault();
    if (currentResponse !== null || leaveDirection !== null) return;
    if (delta > 0 && currentIndex === orderedOptions.length) {
      survey.nextPage();
      return;
    }
    if (delta < 0) {
      const returningOption = orderedOptions[currentIndex - 2];
      setReturnDirection(
        getVote(returningOption) === VOTE_YES ? "right" : "left",
      );
      setTimeout(() => setReturnDirection(null), CARD_ANIMATION_MS);
      setCurrentIndex(currentIndex + delta);
      return;
    }
    setLeaveDirection(getVote(currentOption) === VOTE_YES ? "right" : "left");
    setTimeout(() => {
      setLeaveDirection(null);
      setCurrentIndex(currentIndex + delta);
    }, CARD_ANIMATION_MS);
  };

  const handleResponse = (e, response) => {
    e.preventDefault();
    if (currentResponse !== null) return;
    setCurrentResponse(response);
    const nextIndex = currentIndex + 1;
    if (currentOption !== undefined) {
      store.setAnswer(
        projAnswerKey(currentOption.value),
        response ? VOTE_YES : VOTE_NO,
      );
      store.save().catch((error) => Sentry.captureException(error));
    }

    setTimeout(() => {
      if (currentIndex === orderedOptions.length) {
        survey.nextPage();
        return;
      }
      setCurrentResponse(null);
      setCurrentIndex(nextIndex);
    }, 500);
  };

  const [gradientOpacity, setGradientOpacity] = useState({ left: 0, right: 0 });
  const style = useMemo(
    () => ({
      "--gradient-opacity-right": gradientOpacity.right,
      "--gradient-opacity-left": gradientOpacity.left,
    }),
    [gradientOpacity],
  );

  return (
    <div
      className="swipe"
      style={{ "--card-animation-duration": `${CARD_ANIMATION_MS}ms` }}
    >
      <div style={style} className="swipe__gradient swipe__gradient--left" />
      <div style={style} className="swipe__gradient swipe__gradient--right" />
      <div className="swipe__cards-wrapper">
        {currentIndex === 0 && (
          <Card
            handleResponse={handleResponse}
            className={clsx(
              "card--intro",
              currentResponse !== null && "card--leaving",
            )}
            currentResponse={currentResponse}
            img={instructionImg}
            title={translate("swipe.intro.title")}
            text={translate("swipe.intro.instructions")}
            cta={translate("swipe.intro.cta")}
            key="intro"
            setGradientOpacity={setGradientOpacity}
          />
        )}
        {visibleOptions.map((opt, index) => {
          const project = PROJECTS_BY_ID[opt.value];
          const isTopCard = currentIndex > 0 && index === 0;
          return (
            <Card
              key={opt.value}
              handleResponse={handleResponse}
              className={clsx(
                isTopCard && currentResponse !== null && "card--leaving",
                isTopCard &&
                  returnDirection &&
                  `card--returning card--returning-${returnDirection}`,
                isTopCard &&
                  leaveDirection &&
                  `card--exiting card--exiting-${leaveDirection}`,
              )}
              currentResponse={
                currentIndex !== 0 && index === 0 ? currentResponse : null
              }
              backgroundCard={
                index > 1 ||
                (currentIndex === 0 &&
                  (index > 0 || currentResponse === null)) ||
                (currentIndex !== 0 && index !== 0 && currentResponse === null)
              }
              index={`${Math.max(currentIndex + index, 1)}/${orderedOptions.length}`}
              img={project?.image}
              title={project?.title ?? opt.label}
              text={project?.description}
              category={project?.category}
              postcode={project?.postcode}
              budget={project ? formatBudget(project.budget) : undefined}
              setGradientOpacity={setGradientOpacity}
            />
          );
        })}
      </div>
      <div className="swipe__actions">
        <button
          type="button"
          className={clsx("swipe__nav", !canGoBack && "swipe__nav--hidden")}
          onClick={(e) => handleNavigate(e, -1)}
          aria-label={translate("swipe.previous")}
          disabled={!canGoBack}
        >
          <BackArrowIcon />
        </button>
        <div className="swipe__vote-buttons">
          <div className="swipe__action">
            <button
              type="button"
              className={clsx(
                "button button--round button--grey",
                markedVote === VOTE_YES && "button--faded",
              )}
              onClick={(e) => handleResponse(e, false)}
              aria-label={translate("swipe.reject")}
              aria-pressed={markedVote === VOTE_NO}
            >
              <CloseIcon />
            </button>
            {markedVote === VOTE_NO && (
              <span className="swipe__choice-marker swipe__choice-marker--grey">
                {translate("swipe.my_choice")}
              </span>
            )}
          </div>
          <div className="swipe__action">
            <button
              type="button"
              className={clsx(
                "button button--round",
                markedVote === VOTE_NO && "button--faded",
              )}
              onClick={(e) => handleResponse(e, true)}
              aria-label={translate("swipe.accept")}
              aria-pressed={markedVote === VOTE_YES}
            >
              <HeartIcon />
            </button>
            {markedVote === VOTE_YES && (
              <span className="swipe__choice-marker">
                {translate("swipe.my_choice")}
              </span>
            )}
          </div>
        </div>
        <button
          type="button"
          className={clsx("swipe__nav", !canGoForward && "swipe__nav--hidden")}
          onClick={(e) => handleNavigate(e, 1)}
          aria-label={translate("swipe.next")}
          disabled={!canGoForward}
        >
          <ArrowRightIcon aria-hidden="true" />
        </button>
      </div>
      <p className="swipe__counter">
        {`${translate("swipe.counter")} : ${yesCount}`}
      </p>
      {viewCount >= MIN_PROJECTS_FOR_OVERVIEW && (
        <button
          type="button"
          className="button button--link swipe__overview-link"
          onClick={() => survey.nextPage()}
        >
          {translate("swipe.to_overview")}
        </button>
      )}
    </div>
  );
}
